import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import { useAuth } from '../contexts/AuthContext';
import { Receipt, Download, Eye, DollarSign } from 'lucide-react';
import { formatDate, formatCurrency, delay } from '../utils/helpers';
import { generateFacturaPDF } from '../services/pdfService';
import toast from 'react-hot-toast';

const FacturacionPage = () => {
  const { mockData, updateMockData } = useAuth();
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGenerarFactura = async () => {
    setLoading(true);
    await delay(1500);

    const averiasFacturables = mockData.averias.filter(a => a.estado === 'finalizada' && !a.facturada);
    
    if (averiasFacturables.length === 0) {
      toast.error('No hay averías pendientes de facturar');
      setLoading(false);
      return;
    }

    const importeBase = averiasFacturables.reduce((sum, a) => sum + (a.costeReal || a.costeEstimado), 0);
    const iva = importeBase * 0.21;
    const retencion = importeBase * 0.15;
    const total = importeBase + iva - retencion;

    const nuevaFactura = {
      id: `fact-${Date.now()}`,
      numeroFactura: `F-${new Date().getFullYear()}-${String(mockData.facturas.length + 1).padStart(4, '0')}`,
      fecha: new Date().toISOString(),
      importeBase,
      iva,
      retencion,
      total,
      estado: 'pendiente',
      averiasIds: averiasFacturables.map(a => a.id)
    };

    const updatedFacturas = [nuevaFactura, ...mockData.facturas];
    const updatedAverias = mockData.averias.map(a =>
      averiasFacturables.find(af => af.id === a.id)
        ? { ...a, estado: 'facturada', facturada: true }
        : a
    );

    updateMockData({ facturas: updatedFacturas, averias: updatedAverias });
    
    generateFacturaPDF(nuevaFactura, averiasFacturables);
    
    toast.success('Factura generada correctamente');
    setLoading(false);
  };

  const columns = [
    {
      header: 'Nº Factura',
      cell: (row) => (
        <span className="font-mono text-sm font-semibold">{row.numeroFactura}</span>
      )
    },
    {
      header: 'Fecha',
      cell: (row) => formatDate(row.fecha)
    },
    {
      header: 'Base Imponible',
      cell: (row) => formatCurrency(row.importeBase)
    },
    {
      header: 'IVA',
      cell: (row) => formatCurrency(row.iva)
    },
    {
      header: 'Retención',
      cell: (row) => formatCurrency(row.retencion)
    },
    {
      header: 'Total',
      cell: (row) => (
        <span className="font-semibold text-lg">{formatCurrency(row.total)}</span>
      )
    },
    {
      header: 'Estado',
      cell: (row) => (
        <Badge variant={row.estado === 'pagada' ? 'success' : row.estado === 'pendiente' ? 'warning' : 'danger'}>
          {row.estado.toUpperCase()}
        </Badge>
      )
    },
    {
      header: 'Acciones',
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            icon={Eye}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedFactura(row);
              setShowDetalleModal(true);
            }}
          >
            Ver
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={Download}
            onClick={(e) => {
              e.stopPropagation();
              const averias = mockData.averias.filter(a => row.averiasIds?.includes(a.id));
              generateFacturaPDF(row, averias);
            }}
          >
            PDF
          </Button>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Facturación</h1>
            <p className="text-gray-600 mt-1">Gestión de facturas y liquidaciones</p>
          </div>
          <Button
            variant="success"
            icon={Receipt}
            onClick={handleGenerarFactura}
            loading={loading}
          >
            Generar Factura
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Facturado</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(mockData?.facturas.reduce((sum, f) => sum + f.total, 0) || 0)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-primary-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pendiente de Cobro</p>
                <p className="text-2xl font-bold text-warning-600">
                  {formatCurrency(mockData?.facturas.filter(f => f.estado === 'pendiente').reduce((sum, f) => sum + f.total, 0) || 0)}
                </p>
              </div>
              <Receipt className="w-8 h-8 text-warning-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Facturas Pagadas</p>
                <p className="text-2xl font-bold text-success-600">
                  {mockData?.facturas.filter(f => f.estado === 'pagada').length || 0}
                </p>
              </div>
              <Receipt className="w-8 h-8 text-success-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Facturas Pendientes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {mockData?.facturas.filter(f => f.estado === 'pendiente').length || 0}
                </p>
              </div>
              <Receipt className="w-8 h-8 text-gray-600" />
            </div>
          </Card>
        </div>

        <Card>
          <Table
            columns={columns}
            data={mockData?.facturas || []}
          />
        </Card>
      </div>

      <Modal
        isOpen={showDetalleModal}
        onClose={() => setShowDetalleModal(false)}
        title={`Detalle de Factura ${selectedFactura?.numeroFactura}`}
      >
        {selectedFactura && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Fecha</p>
                <p className="font-semibold">{formatDate(selectedFactura.fecha)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Estado</p>
                <Badge variant={selectedFactura.estado === 'pagada' ? 'success' : 'warning'}>
                  {selectedFactura.estado.toUpperCase()}
                </Badge>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Imponible:</span>
                  <span className="font-semibold">{formatCurrency(selectedFactura.importeBase)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">IVA (21%):</span>
                  <span className="font-semibold">{formatCurrency(selectedFactura.iva)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Retención (15%):</span>
                  <span className="font-semibold text-danger-600">-{formatCurrency(selectedFactura.retencion)}</span>
                </div>
                <div className="flex justify-between text-lg border-t border-gray-200 pt-2">
                  <span className="font-bold">TOTAL:</span>
                  <span className="font-bold text-primary-600">{formatCurrency(selectedFactura.total)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default FacturacionPage;
